import { Component } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import i18n from "@/i18n";

function DefaultErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] p-8 gap-4">
      <div className="flex items-center gap-3 text-destructive">
        <AlertTriangle className="w-6 h-6 shrink-0" />
        <p className="font-semibold">{t("common:errorBoundary.somethingWentWrong")}</p>
      </div>
      {error && (
        <p className="text-xs text-muted-foreground text-center max-w-md">
          {error.message}
        </p>
      )}
      <Button
        size="sm"
        onClick={() => window.location.reload()}
        data-testid="button-reload-page"
      >
        {t("common:errorBoundary.reloadPage")}
      </Button>
    </div>
  );
}

interface Props {
  children: React.ReactNode;
  label?: string;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.label ? ` — ${this.props.label}` : ""}] Caught error:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <DefaultErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}
